import { useQuery } from "@tanstack/react-query";

interface CoinbaseSpot {
  data?: { amount?: string };
}

interface KrakenTicker {
  result?: Record<string, { c?: [string, string] }>;
}

/** Fetches the current BCH price in USD from Coinbase, falling back to Kraken. */
async function fetchBchUsd(signal: AbortSignal): Promise<number> {
  try {
    const res = await fetch(
      "https://api.coinbase.com/v2/prices/BCH-USD/spot",
      { signal },
    );
    if (res.ok) {
      const json = (await res.json()) as CoinbaseSpot;
      const amount = Number(json.data?.amount);
      if (Number.isFinite(amount) && amount > 0) return amount;
    }
  } catch {
    // fall through to Kraken
  }

  const res = await fetch(
    "https://api.kraken.com/0/public/Ticker?pair=BCHUSD",
    { signal },
  );
  const json = (await res.json()) as KrakenTicker;
  const last = json.result?.BCHUSD?.c?.[0];
  const amount = Number(last);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error("Could not fetch BCH price");
  }
  return amount;
}

/** Live BCH/USD price, refreshed periodically. */
export function useBchPrice() {
  return useQuery({
    queryKey: ["bch-price"],
    queryFn: ({ signal }) => fetchBchUsd(signal),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 2,
  });
}

/** Convert a USD amount to BCH given a price, formatted to a readable string. */
export function usdToBch(usd: number, bchUsd: number): string {
  if (!bchUsd) return "—";
  const bch = usd / bchUsd;
  // Show more precision for small amounts.
  if (bch < 0.01) return bch.toFixed(6);
  if (bch < 1) return bch.toFixed(5);
  return bch.toFixed(4);
}
